import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useGetAllRoleList, useGetRoleActions, useInsertRoleActionsFromRef } from "../../../hooks";
import { Loading, PermissionStatus } from "../../atoms";
import { Table } from "../../molecules";
import { useSetSnackBar } from "../../../store/Snackbar.state";

function RenderStatus(props: any) {
  const { value } = props;
  return <PermissionStatus canCall={value} />;
}

export const RoleTransferTab: React.FC = () => {
  const [sourceRoleId, setSourceRoleId] = useState<number | "">("");
  const [targetRoleId, setTargetRoleId] = useState<number | "">("");
  const [roleActions, setRoleActions] = useState<any[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<GridRowSelectionModel>([]);
  const [queryOptions, setQueryOptions] = useState({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { data: rawRoleList, isLoading: rolesLoading } = useGetAllRoleList("ACTIVE");
  const { mutate: getRoleActions, isLoading: getRoleActionsLoading } = useGetRoleActions();
  const { mutate: insertRoleActionsFromRef, isLoading: transferLoading } = useInsertRoleActionsFromRef();
  const setSnackbar = useSetSnackBar();

  const roleList = useMemo(() => {
    return Array.isArray(rawRoleList?.data)
      ? rawRoleList.data.map((role: { name: string; id: number }) => ({
          label: role.name,
          value: role.id,
        }))
      : [];
  }, [rawRoleList?.data]);

  const handleSourceRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setSourceRoleId(roleId);
    setSelectedActionIds([]);
    
    if (roleId) {
      getRoleActions(
        { roleId },
        {
          onSuccess: (data) => {
            if (data.isSuccess) {
              setRoleActions(data.data || []);
            } else {
              setRoleActions([]);
              setSnackbar({
                severity: "error",
                description: data.message || "Rol action'ları alınamadı",
                isOpen: true,
              });
            }
          },
          onError: () => {
            setRoleActions([]);
            setSnackbar({
              severity: "error",
              description: "Rol action'ları alınırken bir hata oluştu",
              isOpen: true,
            });
          },
        }
      );
    } else {
      setRoleActions([]);
    }
  };

  const handleTargetRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setTargetRoleId(roleId);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "microServiceName", headerName: "Microservice", width: 200 },
      { field: "controllerName", headerName: "Controller", width: 200 },
      { field: "actionName", headerName: "Action", width: 200 },
      { 
        renderCell: RenderStatus, 
        field: "canCall", 
        headerName: "Yetki", 
        width: 150,
        filterable: false,
        sortable: false,
      },
    ];
  }, []);

  // Filtreleme yapısı
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters: any) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }
    return queryParams;
  };

  const previousQueryParams = React.useRef({});

  const handleFilterChange = debounce((filters: any) => {
    if (filters.clearFilter) {
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);
    const allValuesAtLeastThreeChars = Object.values(queryParams).every((value: any) => value?.toString().length >= 3);
    const atLeastOneValueThreeChars = Object.values(queryParams).some((value: any) => value?.toString().length >= 3);
    
    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if ((allValuesAtLeastThreeChars || (atLeastOneValueThreeChars && previousKeysLength > currentKeysLength))) {
      setQueryOptions(queryParams);
    }

    previousQueryParams.current = queryParams;
  }, 500);

  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;

  // Filtrelenmiş data
  const filteredRoleActions = useMemo(() => {
    if (!queryOptions || Object.keys(queryOptions).length === 0) {
      return roleActions;
    }

    return roleActions.filter((action: any) => {
      return Object.entries(queryOptions).every(([key, value]: [string, any]) => {
        const fieldValue = action[key]?.toString().toLowerCase() || "";
        const searchValue = value.toString().toLowerCase();
        return fieldValue.includes(searchValue);
      });
    });
  }, [roleActions, queryOptions]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleTransfer = () => {
    if (!sourceRoleId || !targetRoleId) {
      setSnackbar({
        severity: "error",
        description: "Lütfen kaynak ve hedef rolleri seçiniz",
        isOpen: true,
      });
      return;
    }

    if (sourceRoleId === targetRoleId) {
      setSnackbar({
        severity: "error",
        description: "Kaynak ve hedef rol aynı olamaz",
        isOpen: true,
      });
      return;
    }

    if (selectedActionIds.length === 0) {
      setSnackbar({
        severity: "warning",
        description: "Lütfen aktarılacak action'ları seçiniz",
        isOpen: true,
      });
      return;
    }

    const resourceIds = selectedActionIds.join(",");
    
    insertRoleActionsFromRef(
      {
        resourceRoleId: sourceRoleId as number,
        targetRoleId: targetRoleId as number,
        resourceIds: resourceIds,
      },
      {
        onSuccess: (transferData) => {
          if (transferData.isSuccess) {
            setSnackbar({
              severity: "success",
              description: transferData.message || "Seçilen action'lar başarıyla aktarıldı",
              isOpen: true,
            });
            // Seçimleri temizle ve kaynak rol tablosunu yeniden çek
            setSelectedActionIds([]);
            if (sourceRoleId) {
              getRoleActions(
                { roleId: sourceRoleId as number },
                {
                  onSuccess: (refetchData) => {
                    if (refetchData.isSuccess) {
                      setRoleActions(refetchData.data || []);
                    }
                  }
                }
              );
            }
          } else {
            setSnackbar({
              severity: "error",
              description: transferData.message || "Aktarım işlemi başarısız oldu",
              isOpen: true,
            });
          }
        },
        onError: (error) => {
          setSnackbar({
            severity: "error",
            description: "Aktarım işlemi sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const isLoading = rolesLoading || getRoleActionsLoading || transferLoading;
  const selectedRole = roleList.find((role: { label: string; value: number }) => role.value === sourceRoleId);

  return (
    <Box p={3}>
      {isLoading && <Loading />}
      
      <Typography variant="h6" gutterBottom>
        Rol Yetkisi Seçerek Aktarma
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Kaynak rolden istediğiniz action'ları seçerek hedef role aktarın.
      </Typography>

      <Stack spacing={3}>
        <Stack direction="row" spacing={3} maxWidth={800}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="source-role-select-label">Kaynak Rol</InputLabel>
            <Select
              labelId="source-role-select-label"
              value={sourceRoleId}
              label="Kaynak Rol"
              onChange={handleSourceRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="target-role-select-label">Hedef Rol</InputLabel>
            <Select
              labelId="target-role-select-label"
              value={targetRoleId}
              label="Hedef Rol"
              onChange={handleTargetRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {sourceRoleId && roleActions.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedRole?.label} Rolü Action'ları ({filteredRoleActions.length} adet)
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip 
                  label={`${selectedActionIds.length} seçildi`} 
                  color={selectedActionIds.length > 0 ? "primary" : "default"}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTransfer}
                  disabled={!sourceRoleId || !targetRoleId || selectedActionIds.length === 0 || isLoading}
                  size="small"
                >
                  Aktar ({selectedActionIds.length})
                </Button>
              </Stack>
            </Box>

            <Table
              columnVisibilityConfigKey="RoleTransferActionListingColumnVisibilityKey"
              handleFilterChange={handleFilterChange}
              hasActiveFilters={hasActiveFilters}
              paginationModel={paginationModel}
              onPaginationModelChange={handleChangePagination}
              paginationMode="client"
              rowCount={filteredRoleActions.length}
              sx={{ width: isDesktop ? 1308 : window.innerWidth - 50, height: 600 }}
              checkboxSelection
              onRowSelectionModelChange={setSelectedActionIds}
              rowSelectionModel={selectedActionIds}
              rows={filteredRoleActions}
              columns={columns}
              getRowClassName={(params: any) => {
                if (params.row.canCall === false) return 'pasive-row';
                return '';
              }}
            />
          </>
        )}

        {sourceRoleId && roleActions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Seçilen rolde action bulunmamaktadır.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};